import {
  FUEL_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
  type FuelType,
  type VehicleType,
  type ProcessType,
  type FugitiveGasType,
  type RefrigerantGasType,
} from '@g17eco/emissions-config';
import type { SourceSubTypeField } from '../types/emissions';
import { formatActivityType } from '../config/activityTypeBranding';

export interface ResolvedSourceLabels {
  groupLabel: string;
  /** `null` when this is a group-level row (e.g. Purchased Electricity). */
  sourceLabel: string | null;
}

/**
 * Map `(activityType, subTypeField, subTypeValue)` → display labels.
 *
 * - `groupLabel` is the activity-type display name (e.g. "Stationary Combustion").
 * - `sourceLabel` is the sub-type display name (e.g. "Natural Gas"), or `null` for
 *   group-level rows (purchased_*, or Scope 3 v1).
 * - When the subTypeValue is missing/unknown for a type that should have one
 *   (data-quality gap), returns "Unspecified" so the CO₂e is still rendered.
 */
export function resolveSourceLabel(
  activityType: string,
  subTypeField: SourceSubTypeField,
  subTypeValue: string | null,
): ResolvedSourceLabels {
  const groupLabel = formatActivityType(activityType);

  if (subTypeField === null) {
    return { groupLabel, sourceLabel: null };
  }

  if (subTypeValue === null || subTypeValue === '') {
    return { groupLabel, sourceLabel: 'Unspecified' };
  }

  switch (subTypeField) {
    case 'fuelType':
      return {
        groupLabel,
        sourceLabel: FUEL_TYPE_LABELS[subTypeValue as FuelType] ?? titleCase(subTypeValue),
      };
    case 'vehicleType':
      return {
        groupLabel,
        sourceLabel: VEHICLE_TYPE_LABELS[subTypeValue as VehicleType] ?? titleCase(subTypeValue),
      };
    case 'refrigerantType':
      return {
        groupLabel,
        sourceLabel:
          GAS_TYPE_LABELS[subTypeValue as FugitiveGasType | RefrigerantGasType] ??
          titleCase(subTypeValue),
      };
    case 'processType':
      return {
        groupLabel,
        sourceLabel: PROCESS_TYPE_LABELS[subTypeValue as ProcessType] ?? titleCase(subTypeValue),
      };
  }
}

const titleCase = (s: string): string =>
  s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
